import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';
import { Pagination } from '../../../components/shared';
import { format } from 'date-fns';
import { useMemo } from 'react';

export interface Transaction {
  id: string;
  transactionId: string;
  customerName: string;
  customerInitials?: string;
  productName: string;
  amount: number;
  status: 'completed' | 'pending' | 'cancelled' | 'refunded';
  createdAt: string;
  /**
   * Cổng thanh toán (momo / vnpay / mock…). Không có cột riêng — đưa vào tooltip
   * của mã giao dịch, vì đây là thứ phân biệt hai giao dịch trông giống hệt nhau.
   */
  provider?: string;
}

interface RecentTransactionsTableProps {
  transactions: Transaction[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  className?: string;
}

const statusStyles = {
  completed: { bg: 'bg-[#E8F5E9]', text: 'text-[#2E7D32]', label: 'Completed' },
  pending:   { bg: 'bg-[#FFF8E1]', text: 'text-[#F59E0B]', label: 'Pending' },
  cancelled: { bg: 'bg-[#FFEBEE]', text: 'text-[#E8472A]', label: 'Cancelled' },
  refunded:  { bg: 'bg-[#FFF0ED]', text: 'text-[#E8472A]', label: 'Refunded' },
};

const AVATAR_COLORS = [
  '#4F46E5', '#0288D1', '#2E7D32', '#F59E0B',
  '#E8472A', '#9C27B0', '#00897B', '#E91E63',
];

function getInitials(name: string): string {
  return name.split(' ').map((p) => p.charAt(0)).join('').toUpperCase().slice(0, 2);
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Có GIÂY, không chỉ phút.
 *
 * Cắt xuống phút từng làm hai giao dịch khác hẳn nhau (khác đơn, khác cổng
 * thanh toán, cách nhau 0,8 giây) hiện ra y hệt nhau và bị báo là dữ liệu trùng.
 */
function formatDateTime(dateStr: string): string {
  try { return format(new Date(dateStr), 'yyyy-MM-dd HH:mm:ss'); }
  catch { return dateStr; }
}

/**
 * Bug #28: bản cũ vứt bỏ mã thật rồi ghép "#TXN-" + 4 CHỮ SỐ đầu moi từ id. Với
 * id dạng UUID, kết quả là một mã bịa — không tra cứu được ở đâu và hai giao
 * dịch khác nhau còn có thể ra cùng một mã. Giờ hiển thị đúng mã của giao dịch,
 * chỉ rút gọn phần đuôi cho vừa cột.
 */
function formatTxnId(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return '—';
  if (trimmed.startsWith('#')) return trimmed;
  return trimmed.length > 14 ? `#${trimmed.slice(0, 12)}…` : `#${trimmed}`;
}

export function RecentTransactionsTable({
  transactions,
  currentPage,
  pageSize,
  isLoading = false,
  onPageChange,
  className,
}: RecentTransactionsTableProps) {
  const { t } = useI18n();
  const money = useMoney();

  // Giữ NGUYÊN thứ tự nhận được (mới nhất trước, do DashboardPage sắp theo
  // requested_at). Bản cũ sắp lại theo số tiền giảm dần, nên bảng tên là "Recent
  // Transactions" mà dòng đầu lại là giao dịch to nhất chứ không phải mới nhất.
  const totalPages = Math.ceil(transactions.length / pageSize);
  const paginated = useMemo(
    () => transactions.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [transactions, currentPage, pageSize]
  );

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Header. Ô Search và nút "filter" (thực chất là sort theo số tiền) đều
          đã gỡ theo yêu cầu — bảng chỉ liệt kê giao dịch mới nhất trong khoảng
          ngày đang chọn trên header dashboard. */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
        <h3 className="font-sans text-base font-bold text-[#1A1A1A]">{t('recentTransactions')}</h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50/60">
              {[
                { key: 'transactionId', label: t('transactionId') },
                { key: 'customer', label: t('customer') },
                { key: 'product', label: t('product') },
                { key: 'amount', label: t('amount') },
                { key: 'status', label: t('status') },
                { key: 'action', label: t('action') },
              ].map((h) => (
                <th
                  key={h.key}
                  className={cn(
                    'px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.55px] text-slate-400',
                    h.key === 'amount' ? 'text-right' : 'text-left'
                  )}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  {Array.from({ length: 6 }).map((__, c) => (
                    <td key={c} className="px-6 py-4">
                      <div className="h-4 animate-pulse rounded bg-slate-200" style={{ width: `${60 + c * 10}px` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="text-slate-200">
                      <rect x="6" y="8" width="28" height="26" rx="3" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16h16M12 22h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <p className="text-sm font-medium text-slate-400">
                      {t('noTransactionsFound')}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((txn) => {
                const st = statusStyles[txn.status];
                const initials = txn.customerInitials || getInitials(txn.customerName);
                const avatarColor = getAvatarColor(txn.customerName);
                return (
                  <tr key={txn.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50/50">
                    <td className="px-6 py-3.5">
                      <span
                        className="text-sm font-semibold text-[#212121]"
                        title={txn.provider ? `${txn.transactionId}\n${txn.provider}` : txn.transactionId}
                      >
                        {formatTxnId(txn.transactionId)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {initials}
                        </div>
                        <span className="text-sm  text-[#555]">{txn.customerName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-sm text-[#555]">{txn.productName}</span>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <span className="text-sm font-semibold text-[#212121]">{money.format(txn.amount)}</span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className={cn('inline-block rounded-md px-2.5 py-0.5 text-xs font-semibold', st.bg, st.text)}>
                        {t(txn.status as 'pending' | 'completed' | 'cancelled' | 'refunded')}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-sm text-[#9E9E9E]">{formatDateTime(txn.createdAt)}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {!isLoading && paginated.length > 0 && (
        <Pagination
          variant="numbered"
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={transactions.length}
          pageSize={pageSize}
          onPageChange={onPageChange}
          itemLabel={t('transactions')}
          className="border-t border-slate-100"
        />
      )}
    </div>
  );
}
